/**
 * Real V5 runtime loader (directive §1/§2/§3). Loads the runtime-handoff-v5 GLBs in
 * the required order under ONE stable root (StadiumV5Root), parses the data
 * contracts, logs `[Stadium] Loaded runtime-handoff-v5`, and FAILS LOUD — it never
 * falls back to V4. Byte-based progress (§3), no per-module offsets (§4).
 *
 * The GLB/JSON fetchers are injected so the load ORDER, root mounting and fail-loud
 * behaviour are unit-testable without WebGL; the browser app passes a GLTFLoader
 * (KTX2 + meshopt) wrapper.
 */
import { Object3D } from 'three';
import { LOAD_PLAN, type LoadAsset } from './loaderPlan.js';
import { parseMarkers, type MarkerSet } from './markers.js';
import { parseStaffPlacements, type StaffParseResult } from './staffPlacements.js';
import { parseRainOcclusion, type RainOcclusionVolume } from './rainOcclusion.js';

export interface GlbResult { scene: Object3D; }
export type GlbLoadFn = (url: string, onBytes?: (loaded: number, total: number) => void) => Promise<GlbResult>;
export type JsonLoadFn = (url: string) => Promise<unknown>;

/** The module load order (§1): critical → secondary → optional. Must match every
 *  key in `LOAD_PLAN` exactly (`assetByKey` throws on a key that's fallen out of
 *  sync between the two). */
export const V5_LOAD_ORDER: string[] = [
  'core', 'pitch', 'goals', 'collision', 'markers',
  'roof', 'seats', 'props', 'crowdPlacements', 'staffPlacements',
  'goalnets', 'ads', 'scoreboard', 'flagsVeryBig',
  'exterior',
];

export interface StadiumV5Loaded {
  root: Object3D;
  modules: Map<string, Object3D>;
  markers: MarkerSet;
  staff: StaffParseResult;
  rainOcclusion: RainOcclusionVolume[];
  /** Raw crowd placements JSON (parsed by CrowdSystem.setPlacements). */
  crowdPlacementsJson: unknown;
  /** Raw very-big-flag placements JSON (parsed by FlagsSystem.setPlacements). */
  flagsVeryBigPlacementsJson: unknown;
  bytesLoaded: number;
  /** The ACTUAL loaded manifest version (e.g. "runtime-handoff-v5.1"), read from
   *  the synced asset-manifest.json — never hard-coded. */
  version: string;
}

export interface LoadCallbacks {
  onProgress?: (fraction: number) => void;
  onStage?: (key: string, stage: LoadAsset['stage']) => void;
  /** Fires once stage-1 (core/pitch/goals/collision/markers) is ready — the player
   *  may enter while stage-2 streams in (§3). */
  onCanEnter?: () => void;
  log?: (msg: string) => void;
}

function assetByKey(key: string): LoadAsset {
  const a = LOAD_PLAN.find((x) => x.key === key);
  if (!a) throw new Error(`StadiumV5Loader: no load-plan entry for "${key}"`);
  return a;
}

export class StadiumV5Loader {
  readonly root = new Object3D();
  private readonly modules = new Map<string, Object3D>();
  private readonly downloaded = new Map<string, number>();

  constructor(
    private readonly baseUrl: string,
    private readonly loadGlb: GlbLoadFn,
    private readonly loadJson: JsonLoadFn,
  ) {
    this.root.name = 'StadiumV5Root';
  }

  private totalBytes(): number {
    return V5_LOAD_ORDER.reduce((s, k) => s + assetByKey(k).approxBytes, 0);
  }

  private reportProgress(cb?: LoadCallbacks): void {
    if (!cb?.onProgress) return;
    let got = 0;
    for (const k of V5_LOAD_ORDER) got += Math.min(assetByKey(k).approxBytes, this.downloaded.get(k) ?? 0);
    cb.onProgress(got / this.totalBytes());
  }

  /**
   * Load everything in order. Resolves once ALL modules are mounted; `onCanEnter`
   * fires earlier, when the gameplay-critical set is ready. Any failure rejects
   * (fail loud) — there is deliberately no V4 fallback path.
   */
  async load(cb: LoadCallbacks = {}): Promise<StadiumV5Loaded> {
    const log = cb.log ?? ((m: string) => console.info(m));
    // The ACTUAL version from the synced manifest — reported everywhere, never
    // hard-coded, so V5.1 (and later) shows correctly.
    let version = 'runtime-handoff-v5';
    try {
      const am = (await this.loadJson(`${this.baseUrl}/asset-manifest.json`)) as { handoffVersion?: string };
      if (am?.handoffVersion) version = am.handoffVersion;
    } catch { /* asset-manifest optional; fall back to the generic label */ }
    let markers: MarkerSet | null = null;
    let staff: StaffParseResult | null = null;
    let crowdJson: unknown = null;
    let flagsJson: unknown = null;

    const criticalKeys = new Set(['core', 'pitch', 'goals', 'collision', 'markers']);
    let criticalDone = 0;

    for (const key of V5_LOAD_ORDER) {
      const asset = assetByKey(key);
      cb.onStage?.(key, asset.stage);
      const url = `${this.baseUrl}/${asset.path}`;
      try {
        if (asset.kind === 'glb') {
          const res = await this.loadGlb(url, (loaded, total) => {
            this.downloaded.set(key, total > 0 ? loaded : asset.approxBytes);
            this.reportProgress(cb);
          });
          res.scene.name = `V5_${key}`;
          this.modules.set(key, res.scene);
          this.root.add(res.scene); // shared origin — no per-module offset (§4)
        } else {
          const json = await this.loadJson(url);
          if (key === 'markers') markers = parseMarkers(json);
          else if (key === 'staffPlacements') staff = parseStaffPlacements(json);
          else if (key === 'crowdPlacements') crowdJson = json;
          else if (key === 'flagsVeryBigPlacements') flagsJson = json;
        }
        this.downloaded.set(key, asset.approxBytes);
        this.reportProgress(cb);
      } catch (e) {
        // FAIL LOUD (§1) — never fall back to V4.
        const msg = e instanceof Error ? e.message : String(e);
        throw new Error(`[Stadium] FAILED to load V5 "${key}" from ${url}: ${msg} (no V4 fallback)`);
      }
      if (criticalKeys.has(key) && ++criticalDone === criticalKeys.size) {
        log('[Stadium] runtime-handoff-v5 gameplay-critical assets ready — enter enabled');
        cb.onCanEnter?.();
      }
    }

    if (!markers) throw new Error('[Stadium] V5 markers.json never loaded');
    // Rain occlusion is a separate data file (not in the module order); load it now.
    const rainOcc = parseRainOcclusion(await this.loadJson(`${this.baseUrl}/data/rain_occlusion_volumes.json`));

    log(`[Stadium] Loaded ${version}`);
    return {
      root: this.root,
      modules: this.modules,
      markers,
      staff: staff ?? { staff: [], roles: [], insidePlayingArea: [] },
      rainOcclusion: rainOcc,
      crowdPlacementsJson: crowdJson,
      flagsVeryBigPlacementsJson: flagsJson,
      bytesLoaded: this.totalBytes(),
      version,
    };
  }

  getModule(key: string): Object3D | undefined { return this.modules.get(key); }
}
